import { useState, useEffect, useCallback } from "react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from "recharts";
import {
  TrendingUp, TrendingDown, Users, Eye, MousePointerClick, Clock,
  Globe, Smartphone, Monitor, Tablet, ArrowUpRight, MapPin
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authH() { return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }; }

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16"];
const PERIODS = [
  { value: "7d", label: "7 jours" },
  { value: "30d", label: "30 jours" },
  { value: "90d", label: "90 jours" },
  { value: "1y", label: "1 an" },
];

function Change({ value }) {
  if (!value || value === 0) return <span className="text-white/30 text-xs">—</span>;
  const up = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${up ? "text-emerald-400" : "text-red-400"}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {up ? "+" : ""}{value}%
    </span>
  );
}

function MetricCard({ icon: Icon, label, value, change, accent }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="w-4 h-4" />
        </div>
        <Change value={change} />
      </div>
      <p className="text-2xl font-bold text-white font-['Outfit']">{value}</p>
      <p className="text-white/40 text-[11px] uppercase tracking-wider font-['Manrope'] mt-1">{label}</p>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0A2540] border border-white/20 rounded px-3 py-2 text-xs font-['Manrope']">
      <p className="text-white/50">{label}</p>
      <p className="text-white font-semibold">{payload[0].value}</p>
    </div>
  );
}

function DonutChart({ data, title }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
      <h4 className="text-white/60 text-xs uppercase tracking-wider font-['Manrope'] mb-3">{title}</h4>
      <div className="flex items-center gap-4">
        <ResponsiveContainer width={100} height={100}>
          <PieChart>
            <Pie data={data} dataKey="count" nameKey="_id" innerRadius={28} outerRadius={45} paddingAngle={2}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="flex-1 space-y-1.5">
          {data.slice(0, 5).map((d, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
              <span className="text-white/70 font-['Manrope'] truncate flex-1">{d._id || "Autre"}</span>
              <span className="text-white/40 font-['Manrope']">{total ? Math.round(d.count / total * 100) : 0}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AnalyticsTab() {
  const [period, setPeriod] = useState("30d");
  const [overview, setOverview] = useState(null);
  const [chart, setChart] = useState([]);
  const [chartMetric, setChartMetric] = useState("visitors");
  const [pages, setPages] = useState([]);
  const [sources, setSources] = useState([]);
  const [devices, setDevices] = useState(null);
  const [locations, setLocations] = useState(null);
  const [commercial, setCommercial] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const h = authH();
      const [ov, ch, pg, sr, dv, loc, com] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview?period=${period}`, { headers: h }),
        axios.get(`${API}/admin/analytics/chart?period=${period}&metric=${chartMetric}`, { headers: h }),
        axios.get(`${API}/admin/analytics/pages?period=${period}`, { headers: h }),
        axios.get(`${API}/admin/analytics/sources?period=${period}`, { headers: h }),
        axios.get(`${API}/admin/analytics/devices?period=${period}`, { headers: h }),
        axios.get(`${API}/admin/analytics/locations?period=${period}`, { headers: h }),
        axios.get(`${API}/admin/analytics/commercial?period=${period}`, { headers: h }),
      ]);
      setOverview(ov.data);
      setChart(ch.data.data);
      setPages(pg.data.pages);
      setSources(sr.data.sources);
      setDevices(dv.data);
      setLocations(loc.data);
      setCommercial(com.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [period, chartMetric]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading && !overview) return <div className="text-center text-white/30 py-12 font-['Manrope']">Chargement des analytics...</div>;

  return (
    <div data-testid="analytics-tab" className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center gap-2">
        {PERIODS.map(p => (
          <button key={p.value} onClick={() => setPeriod(p.value)}
            className={`px-3 py-1.5 rounded text-xs font-['Manrope'] transition ${period === p.value ? "bg-white/15 text-white border border-white/20" : "text-white/40 hover:text-white/60"}`}
          >{p.label}</button>
        ))}
      </div>

      {overview && (
        <>
          {/* Key metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <MetricCard icon={Users} label="Visiteurs uniques" value={overview.unique_visitors} change={overview.changes?.unique} accent="bg-blue-500/10 text-blue-400" />
            <MetricCard icon={Eye} label="Pages vues" value={overview.total_views} change={overview.changes?.views} accent="bg-purple-500/10 text-purple-400" />
            <MetricCard icon={MousePointerClick} label="Demandes de devis" value={overview.total_quotes} change={overview.changes?.quotes} accent="bg-emerald-500/10 text-emerald-400" />
            <MetricCard icon={ArrowUpRight} label="Taux de conversion" value={`${overview.conversion_rate}%`} accent="bg-amber-500/10 text-amber-400" />
            <MetricCard icon={Clock} label="Durée moyenne" value={`${overview.avg_duration}s`} accent="bg-cyan-500/10 text-cyan-400" />
          </div>

          {/* Today + bounce */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard icon={Users} label="Visiteurs aujourd'hui" value={overview.visitors_today} accent="bg-white/10 text-white" />
            <MetricCard icon={Eye} label="Pages vues aujourd'hui" value={overview.views_today} accent="bg-white/10 text-white" />
            <MetricCard icon={Users} label="Visiteurs récurrents" value={overview.recurring_visitors} accent="bg-indigo-500/10 text-indigo-400" />
            <MetricCard icon={TrendingDown} label="Taux de rebond" value={`${overview.bounce_rate}%`} accent="bg-red-500/10 text-red-400" />
          </div>
        </>
      )}

      {/* Main chart */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold font-['Outfit'] text-sm">Évolution du trafic</h3>
          <div className="flex gap-1">
            {["visitors", "pageviews", "quotes"].map(m => (
              <button key={m} onClick={() => setChartMetric(m)}
                className={`px-2.5 py-1 rounded text-[11px] font-['Manrope'] transition ${chartMetric === m ? "bg-white/15 text-white" : "text-white/30 hover:text-white/50"}`}
              >{{visitors: "Visiteurs", pageviews: "Pages vues", quotes: "Devis"}[m]}</button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={chart}>
            <defs>
              <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.3)" }} tickFormatter={v => v.slice(5)} />
            <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.3)" }} width={35} />
            <Tooltip content={<ChartTooltip />} />
            <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="url(#chartGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Sources + Devices row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {sources.length > 0 && <DonutChart data={sources} title="Sources de trafic" />}
        {devices?.devices?.length > 0 && <DonutChart data={devices.devices} title="Appareils" />}
        {devices?.browsers?.length > 0 && <DonutChart data={devices.browsers} title="Navigateurs" />}
      </div>

      {/* Pages + Locations + Commercial */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top pages */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <h4 className="text-white/60 text-xs uppercase tracking-wider font-['Manrope'] mb-3">Pages les plus consultées</h4>
          <div className="space-y-2">
            {pages.slice(0, 8).map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-white/20 text-xs w-5 text-right font-['Manrope']">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full bg-blue-500/40" style={{ width: `${pages[0]?.views ? (p.views / pages[0].views * 100) : 0}%` }} />
                  </div>
                </div>
                <span className="text-white/60 text-xs font-['Manrope'] truncate max-w-[150px]">{p.page === "/" ? "Accueil" : p.page}</span>
                <span className="text-white/30 text-xs font-['Manrope']">{p.views}</span>
              </div>
            ))}
            {pages.length === 0 && <p className="text-white/20 text-xs font-['Manrope']">Aucune donnée</p>}
          </div>
        </div>

        {/* Locations */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <h4 className="text-white/60 text-xs uppercase tracking-wider font-['Manrope'] mb-3">
            <MapPin className="w-3 h-3 inline mr-1" />Localisation des visiteurs
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-white/30 text-[10px] uppercase tracking-wider mb-2 font-['Manrope']">Villes</p>
              <div className="space-y-1.5">
                {(locations?.cities || []).slice(0, 6).map((c, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-white/60 font-['Manrope']">{c._id}</span>
                    <span className="text-white/30 font-['Manrope']">{c.count}</span>
                  </div>
                ))}
                {!locations?.cities?.length && <p className="text-white/20 text-xs font-['Manrope']">—</p>}
              </div>
            </div>
            <div>
              <p className="text-white/30 text-[10px] uppercase tracking-wider mb-2 font-['Manrope']">Pays</p>
              <div className="space-y-1.5">
                {(locations?.countries || []).slice(0, 6).map((c, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-white/60 font-['Manrope']">{c._id}</span>
                    <span className="text-white/30 font-['Manrope']">{c.count}</span>
                  </div>
                ))}
                {!locations?.countries?.length && <p className="text-white/20 text-xs font-['Manrope']">—</p>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Commercial performance */}
      {commercial && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-5">
          <h4 className="text-white/60 text-xs uppercase tracking-wider font-['Manrope'] mb-4">Performance commerciale</h4>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-2xl font-bold text-white font-['Outfit']">{commercial.total_quotes}</p>
              <p className="text-white/40 text-[11px] font-['Manrope']">Devis reçus <Change value={commercial.change} /></p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white font-['Outfit']">{commercial.conversion_rate}%</p>
              <p className="text-white/40 text-[11px] font-['Manrope']">Taux de conversion</p>
            </div>
            <div>
              <p className="text-white/60 text-xs font-['Manrope'] mb-2">Prestations demandées</p>
              {commercial.top_services?.slice(0, 4).map((s, i) => (
                <div key={i} className="flex justify-between text-xs mb-1">
                  <span className="text-white/50 font-['Manrope'] truncate mr-2">{s._id}</span>
                  <span className="text-white/30 font-['Manrope']">{s.count}</span>
                </div>
              ))}
            </div>
            <div>
              <p className="text-white/60 text-xs font-['Manrope'] mb-2">Par type</p>
              {commercial.by_type?.map((t, i) => (
                <div key={i} className="flex justify-between text-xs mb-1">
                  <span className="text-white/50 font-['Manrope']">{t._id}</span>
                  <span className="text-white/30 font-['Manrope']">{t.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
